/**
 * Read-only recursive BOM tree for Models detail (theoretical ModelsBomWorkspace center pane).
 */
import { useEffect, useState } from "react";
import { getManufacturingRecipeDetail } from "@/lib/manufacturing/recipesClient";
import type { MfgRecipeLineDetail } from "@/lib/manufacturing/types";

type TreeNodeProps = {
  recipeId: string;
  depth: number;
};

function TreeNode({ recipeId, depth }: TreeNodeProps) {
  const [lines, setLines] = useState<MfgRecipeLineDetail[] | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await getManufacturingRecipeDetail(recipeId);
      if (cancelled) return;
      if (!r.ok) {
        setError(r.error);
        setLines([]);
        return;
      }
      setName(r.data.recipe.name);
      setLines(r.data.lines);
    })();
    return () => {
      cancelled = true;
    };
  }, [recipeId]);

  if (error) {
    return (
      <div className="text-xs text-red-600" style={{ paddingLeft: depth * 16 }}>
        {error}
      </div>
    );
  }
  if (!lines) {
    return (
      <div className="text-xs text-gray-500" style={{ paddingLeft: depth * 16 }}>
        Loading…
      </div>
    );
  }

  return (
    <div>
      {depth === 0 ? (
        <div className="mb-1 text-xs font-semibold text-gray-800 dark:text-zinc-200">{name}</div>
      ) : null}
      <ul className="space-y-0.5">
        {lines.map((ln) => {
          const isComponent = ln.line_kind === "component" && ln.component_recipe_id;
          const label =
            ln.material_name ||
            ln.material_code ||
            ln.component_recipe_name ||
            ln.component_recipe_code ||
            ln.material_use_name ||
            "Line";
          const open = expanded[ln.id];
          return (
            <li key={ln.id}>
              <div
                className="flex flex-wrap items-center gap-2 py-1 text-xs"
                style={{ paddingLeft: 8 + depth * 16 }}
              >
                {isComponent ? (
                  <button
                    type="button"
                    className="w-5 text-gray-500"
                    onClick={() => setExpanded((e) => ({ ...e, [ln.id]: !e[ln.id] }))}
                  >
                    {open ? "▾" : "▸"}
                  </button>
                ) : (
                  <span className="w-5 text-center text-gray-300">·</span>
                )}
                <span className="min-w-0 flex-1 truncate text-gray-900 dark:text-zinc-100">
                  {label}
                </span>
                <span className="font-mono text-gray-500">×{ln.quantity}</span>
                <span className="rounded border border-gray-200 px-1 text-[10px] uppercase text-gray-500 dark:border-zinc-600">
                  {ln.role}
                </span>
                <span className="font-mono text-gray-700 dark:text-zinc-300">
                  ${ln.extended_cost.toFixed(2)}
                </span>
              </div>
              {isComponent && open && ln.component_recipe_id ? (
                <TreeNode recipeId={ln.component_recipe_id} depth={depth + 1} />
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function ManufacturingModelsBomTree({ recipeId }: { recipeId: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-zinc-700 dark:bg-zinc-900/40">
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        Recursive BOM tree · read-only
      </div>
      <TreeNode recipeId={recipeId} depth={0} />
    </div>
  );
}
